import { toast } from '../../../util/toasts';
import { useCallback, useEffect, useState } from 'react';
import { debounce } from 'lodash';
import { permissionsNames } from '../config/permissions';
import { useAdminApiV1 } from '../../../clients/quipli-service/admin/admin-api-v1';

interface UseAdminPermissionsProps {
  adminId?: number;
  fetchAtMount?: boolean;
}

export const useAdminPermissions = ({ adminId, fetchAtMount = true }: UseAdminPermissionsProps) => {
  const { updateUserAdminPermissions, getUserAdminPermissions } = useAdminApiV1();

  const [isLoading, setIsLoading] = useState<boolean>(fetchAtMount);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [areAllPermissionsSelected, setAreAllPermissionsSelected] = useState<boolean>(false);

  // Save admin permissions with debounce
  const savePermissionSettings = useCallback(
    debounce(async function (permissions: string[], _adminId: number) {
      try {
        setIsSubmitting(true);
        await updateUserAdminPermissions(_adminId, permissions);
        toast('Permissions updated successfully');
        setHasUnsavedChanges(false);
      } catch (e: any) {
        toast('Something wrong updating permissions', 'error');
      } finally {
        setIsSubmitting(false);
      }
    }, 600),
    [],
  );

  // Toggle individual permission
  const togglePermission = useCallback((permission: string, isEnabled: boolean) => {
    setHasUnsavedChanges(true);
    setSelectedPermissions((prevPermissions) => {
      if (isEnabled) {
        return [...prevPermissions, permission];
      } else {
        return prevPermissions.filter((item) => item !== permission);
      }
    });
  }, []);

  // Toggle all permissions
  const toggleAllPermissions = useCallback(() => {
    setHasUnsavedChanges(true);
    setSelectedPermissions((prevPermissions) => {
      const areAllCurrentlySelected = permissionsNames.every((permission) => prevPermissions.includes(permission));

      if (!areAllCurrentlySelected) {
        return [...permissionsNames];
      } else {
        return [];
      }
    });
  }, []);

  // Check if all permissions are selected
  const checkIfAllPermissionsSelected = useCallback((permissions: string[]) => {
    const areAllSelected = permissionsNames.every((permission) => permissions.includes(permission));
    setAreAllPermissionsSelected(areAllSelected);
  }, []);

  // Fetch admin permissions
  const fetchPermissionSettings = useCallback(async (_adminId: number) => {
    try {
      const permissions = await getUserAdminPermissions(_adminId);
      setSelectedPermissions(permissions);
      checkIfAllPermissionsSelected(permissions);
    } catch (e: any) {
      toast('Error fetching permissions', 'error');
    }
  }, []);

  // Initialize the hook
  const initializePermissionSettings = useCallback(async (_adminId: number) => {
    setIsLoading(true);
    await fetchPermissionSettings(_adminId);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (fetchAtMount && adminId) {
      initializePermissionSettings(adminId);
    }
  }, [fetchAtMount, adminId, initializePermissionSettings]);

  useEffect(() => {
    if (!isSubmitting && hasUnsavedChanges && adminId) {
      savePermissionSettings(selectedPermissions, adminId);
      checkIfAllPermissionsSelected(selectedPermissions);
    }
  }, [selectedPermissions, isSubmitting, hasUnsavedChanges, adminId]);

  return {
    // State
    isLoading,
    isSubmitting,
    selectedPermissions,
    areAllPermissionsSelected,

    // Business logic
    togglePermission,
    toggleAllPermissions,
    savePermissionSettings,
    fetchPermissionSettings,
  };
};
